import { useState, useMemo, useContext } from "react";
import PageLayout from "../../components/PageLayout";
import projectsHelp from "../../content/help/projects.json";
import ProjectsToolbar from "./components/ProjectsToolbar";
import ProjectsTable from "./components/ProjectsTable";
import useProjects from "./hooks/useProjects";

import AddEditProjectModal from "./modals/AddEditProjectModal";
import ProjectModulesModal from "./modals/ProjectModulesModal";
import ProjectGraphModal from "./modals/ProjectGraphModal";
import usePageTitle from "../../services/usePageTitle";
import { AuthContext } from "../../services/authcontext";

export default function Projects({ pageTitle = "Projects" }) {
  usePageTitle("SkeyeGate Projects - SkeyeGate™");
  const { user } = useContext(AuthContext);

  const userData = user;
  const roles = userData?.roles?.map((r) => r.name) || [];
  const isManager =
    roles.includes("ROLE_MANAGER") || roles.includes("ROLE_ADMIN");

  const { data, loading, loadData, removeProject } = useProjects(isManager);

  // States
  const [search, setSearch] = useState("");
  const [editModal, setEditModal] = useState({ open: false, project: null });
  const [modulesModal, setModulesModal] = useState({
    open: false,
    project: null,
  });
  const [graphModal, setGraphModal] = useState({ open: false, project: null });

  // Filtering
  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return data;
    return data.filter(
      (p) =>
        p.title?.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term),
    );
  }, [search, data]);

  return (
    <div className="projects-page">
      <PageLayout
        title={pageTitle}
        helpContent={projectsHelp}
        description="Manage Projects, view details."
      >
        <ProjectsToolbar
          search={search}
          onSearchChange={setSearch}
          onRefresh={loadData}
          onCreate={() => setEditModal({ open: true, project: null })}
          isManager={isManager}
        />

        <ProjectsTable
          data={filtered}
          loading={loading}
          isManager={isManager}
          userData={userData}
          onEdit={(p) => setEditModal({ open: true, project: p })}
          onDelete={(p) => removeProject(p.id)}
          onOpenModules={(p) => setModulesModal({ open: true, project: p })}
          onViewGraph={(p) => setGraphModal({ open: true, project: p })}
        />
      </PageLayout>

      {/* MODALS */}
      {editModal.open && (
        <AddEditProjectModal
          project={editModal.project}
          onClose={() => setEditModal({ open: false, project: null })}
          onSuccess={loadData}
        />
      )}

      {modulesModal.open && (
        <ProjectModulesModal
          project={modulesModal.project}
          onClose={() => setModulesModal({ open: false, project: null })}
          onSuccess={loadData}
          isManager={isManager}
          userData={userData}
        />
      )}

      {graphModal.open && (
        <ProjectGraphModal
          project={graphModal.project}
          onClose={() => setGraphModal({ open: false, project: null })}
        />
      )}
    </div>
  );
}
